/**
 * Phase 7.40.8 — Invitations Bell.
 *
 * Header widget that polls `GET /family-groups/invitations` (via React
 * Query, every 60s) and shows an unread badge for pending invitations.
 * Click opens a small popover with Accept / Decline buttons.
 *
 * Hidden when the user has no pending invitations.
 */

'use client';

import { useFamilyInvitations } from '@/hooks/use-family';
import { api } from '@/lib/api';
import { useQueryClient } from '@tanstack/react-query';
import { Bell, Check, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

export function InvitationsBell(): JSX.Element | null {
  const [open, setOpen] = useState(false);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const queryClient = useQueryClient();

  const { data: invitations = [] } = useFamilyInvitations();
  const count = invitations.length;

  useEffect(() => {
    if (!open) return;
    const handlePointer = (e: MouseEvent | TouchEvent): void => {
      const target = e.target as Node | null;
      if (!target) return;
      if (buttonRef.current?.contains(target)) return;
      if (popoverRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('touchstart', handlePointer);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('touchstart', handlePointer);
    };
  }, [open]);

  if (count === 0) return null;

  const refresh = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['family', 'invitations'] }),
      queryClient.invalidateQueries({ queryKey: ['family', 'groups'] }),
    ]);
  };

  const handleAccept = async (id: string): Promise<void> => {
    setPendingId(id);
    try {
      await api.family.acceptInvitation(id);
      toast.success('Invitation accepted');
      await refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to accept invitation');
    } finally {
      setPendingId(null);
    }
  };

  const handleDecline = async (id: string): Promise<void> => {
    setPendingId(id);
    try {
      await api.family.declineInvitation(id);
      toast.success('Invitation declined');
      await refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to decline invitation');
    } finally {
      setPendingId(null);
    }
  };

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative rounded-lg p-2 text-gray-600 hover:bg-gray-100"
        aria-label={`Family invitations (${count} pending)`}
      >
        <Bell className="h-5 w-5" />
        <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-emerald-600 px-1 text-[10px] font-semibold leading-none text-white">
          {count > 9 ? '9+' : count}
        </span>
      </button>

      {open && (
        <div
          ref={popoverRef}
          className="absolute right-0 top-full z-50 mt-2 w-80 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg"
          role="dialog"
          aria-label="Pending family invitations"
        >
          <div className="border-b border-gray-100 px-4 py-3">
            <h3 className="text-sm font-semibold text-gray-900">Family invitations</h3>
            <p className="mt-0.5 text-xs text-gray-500">
              {count} pending invitation{count === 1 ? '' : 's'}
            </p>
          </div>
          <ul className="max-h-96 divide-y divide-gray-100 overflow-y-auto">
            {invitations.map((inv) => (
              <li key={inv.id} className="px-4 py-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-gray-900">
                      {inv.group?.name ?? 'Family group'}
                    </p>
                    <p className="mt-0.5 text-xs text-gray-500">
                      Invited to join by {inv.invitee_email}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <button
                      type="button"
                      onClick={() => handleAccept(inv.id)}
                      disabled={pendingId === inv.id}
                      className="rounded-md bg-emerald-600 p-1.5 text-white hover:bg-emerald-700 disabled:opacity-50"
                      aria-label="Accept invitation"
                      title="Accept"
                    >
                      <Check className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDecline(inv.id)}
                      disabled={pendingId === inv.id}
                      className="rounded-md bg-gray-100 p-1.5 text-gray-700 hover:bg-gray-200 disabled:opacity-50"
                      aria-label="Decline invitation"
                      title="Decline"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
